/**
 * Returns true for errors that should be suppressed from user-facing feedback —
 * they recover on their own (the realtime/visibility refetch retries) and are not
 * real failures the user must act on:
 *  - browser navigation / page refresh / network loss (AbortError + fetch msgs)
 *  - **token expiry** — iOS suspends the PWA's auto-refresh timer while
 *    backgrounded, so the first query on resume can hit an expired JWT (HTTP 401
 *    / PostgREST `PGRST301`). `ensureFreshSession()` refreshes on resume and the
 *    next refetch succeeds; a dead refresh token is handled by AuthContext's
 *    session checks, not here.
 *  - **transient server/gateway errors** — 502/503/504 and Postgres statement
 *    timeout (`57014`), which are retry-later blips, not user-actionable.
 * NOTE: callers must still clear any loading state on the suppress branch (don't
 * leave a skeleton hanging) — only navigation-aborts unmount the component.
 */
export function isTransientError(err) {
  if (!err) return false;
  if (err.name === 'AbortError') return true;

  const status = typeof err.status === 'number' ? err.status : undefined;
  if (status === 401 || status === 502 || status === 503 || status === 504) return true;

  const code = typeof err.code === 'string' ? err.code : undefined;
  if (code === 'PGRST301' /* JWT expired */ || code === '57014' /* statement timeout */) return true;

  const msg = typeof err === 'string' ? err : err.message;
  if (typeof msg !== 'string') return false;
  return /^(TypeError:\s*)?(Load failed|Failed to fetch|NetworkError|The operation was aborted)/i.test(msg)
    || /\b(JWT expired|token is expired|JWSError)\b/i.test(msg)
    || /\b(statement timeout|canceling statement|Service Unavailable|Bad Gateway|Gateway Timeout)\b/i.test(msg);
}

export function getErrorMessage(err, fallback = "Unexpected error") {
  if (!err) return fallback
  if (typeof err === "string") return err
  if (err.message) return err.message
  return fallback
}

export function logError(context, err) {
  console.error("[ERROR]", context, err)
}